import { GrammarHint } from '@/components/word/GrammarHint'
import { useIsPhone } from '@/hooks/useIsPhone'
import { CASE_FRIENDLY, CASE_LABELS, CASE_ORDER, capitalize, formatSlotLabel, joinAlternatives } from '@/utils/grammar'
import type { WordFormInfo } from '@/types/word'

interface NounParadigmProps {
  forms: WordFormInfo[]
}

const NUMBERS = ['singular', 'plural'] as const
type Num = (typeof NUMBERS)[number]

/** `c` is "" in a caseless table. */
function cell(forms: WordFormInfo[], c: string, number: Num, definite: boolean): string | undefined {
  return forms.find(
    (f) =>
      (f.grammatical_info.case ?? '') === c &&
      f.grammatical_info.number === number &&
      (f.grammatical_info.definiteness === 'definite') === definite,
  )?.surface
}

/** A noun that declines for number and definiteness alone (Bokmål): its table is one row. */
function isCaseless(forms: WordFormInfo[]): boolean {
  return (
    forms.some((f) => f.grammatical_info.definiteness === 'definite') &&
    forms.every((f) => !f.grammatical_info.case && !f.grammatical_info.gender && !f.grammatical_info.degree)
  )
}

/**
 * Declension by case. One table on a wide screen; on a phone one table per number,
 * because five columns of Icelandic will not fit in 390px without a sideways scroll.
 */
export function NounParadigm({ forms: stored }: NounParadigmProps) {
  const isPhone = useIsPhone()
  const forms = joinAlternatives(stored)
  const caseless = isCaseless(forms)
  const cases = caseless ? [''] : CASE_ORDER.filter((c) => forms.some((f) => f.grammatical_info.case === c))
  const other = caseless ? [] : forms.filter((f) => !f.grammatical_info.case || !CASE_LABELS[f.grammatical_info.case])
  const definite = forms.some((f) => f.grammatical_info.definiteness === 'definite')
  const numbers = NUMBERS.filter((n) => forms.some((f) => f.grammatical_info.number === n))

  return (
    <div className="space-y-8">
      {cases.length > 0 && (isPhone
        ? numbers.map((n) => (
            <CaseTable key={n} forms={forms} cases={cases} numbers={[n]} definite={definite} caption={capitalize(n)} />
          ))
        : <CaseTable forms={forms} cases={cases} numbers={numbers} definite={definite} />)}

      {other.length > 0 && (
        <section>
          <h4 className="font-mono text-xs uppercase tracking-widest text-muted">Other forms</h4>
          <dl className="mt-3">
            {other.map((f) => (
              <div
                key={f.grammatical_slot}
                className="flex items-baseline justify-between gap-4 border-t border-line-soft py-2"
              >
                <dt className="min-w-0 font-serif text-lg text-heading">{f.surface}</dt>
                <dd className="shrink-0 text-right text-sm text-muted">{formatSlotLabel(f)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}
    </div>
  )
}

interface CaseTableProps {
  forms: WordFormInfo[]
  cases: string[]
  numbers: readonly Num[]
  definite: boolean
  caption?: string
}

function CaseTable({ forms, cases, numbers, definite, caption }: CaseTableProps) {
  const variants = definite ? [false, true] : [false]
  const rowHeaders = cases.some(Boolean)
  return (
    <table className="w-full table-fixed border-collapse">
      {caption && (
        <caption className="pb-3 text-left font-mono text-xs uppercase tracking-widest text-muted">{caption}</caption>
      )}
      <thead>
        {!caption && (
          <tr>
            {rowHeaders && <th scope="col" aria-hidden="true" className="w-[7.5rem]" />}
            {numbers.map((n) => (
              <th
                key={n}
                scope="col"
                colSpan={variants.length}
                className="pb-1 text-left font-mono text-xs uppercase tracking-widest text-muted"
              >
                {capitalize(n)}
              </th>
            ))}
          </tr>
        )}
        {definite && (
          <tr>
            {rowHeaders && <th scope="col" aria-hidden="true" className="w-[7.5rem]" />}
            {numbers.flatMap((n) =>
              variants.map((d) => (
                <th key={`${n}-${d}`} scope="col" className="pb-2 text-left text-xs font-normal text-muted">
                  {d ? 'definite' : 'indefinite'}
                </th>
              )),
            )}
          </tr>
        )}
      </thead>
      <tbody>
        {cases.map((c) => (
          <tr key={c} className="border-t border-line-soft">
            {rowHeaders && (
              <th scope="row" className="py-1 text-left font-normal">
                <GrammarHint label={CASE_LABELS[c]} title={CASE_FRIENDLY[c]} />
              </th>
            )}
            {numbers.flatMap((n) =>
              variants.map((d) => (
                <td key={`${n}-${d}`} className="py-1 pr-3 font-serif text-lg text-heading">
                  {cell(forms, c, n, d) ?? <span className="text-muted/40">&mdash;</span>}
                </td>
              )),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}
